import type { Option } from '@form/FormSelect';
import { isColumnUsable } from 'components/TrainingPlanner/columnAvailability';
import { groupLabel } from 'components/TrainingPlanner/groupName';
import type {
    ColumnAvailability,
    Group,
    TrainingPlannerColumn,
    TrainingPlannerPlan,
    TrainingPlannerTag
} from 'components/TrainingPlanner/types';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

/** Where the plan's tags live, so callers and cache invalidation agree on it. */
export const tagUrl = (clubSlug?: string, planId?: number) =>
    clubSlug && planId ? `club/${clubSlug}/training-planner/plan/${planId}/tag` : null;

/**
 * Tags replace the single free-text level: a group can carry its level, its
 * season and an age band at once.
 *
 * Labels and nothing else. A tag used to hold a price as well, and that price
 * now lives in the plan's price list with the tag as its condition — which is
 * also why the list belongs to the plan rather than the club: a rule can only
 * point at a tag of the plan it prices.
 */
export const useTagOptions = (clubSlug?: string, planId?: number) => {
    const { data, isLoading, mutate } = useSWR<{ data: TrainingPlannerTag[] }>(tagUrl(clubSlug, planId));

    const tags = useMemo(() => data?.data ?? [], [data]);
    const options: Option[] = useMemo(() => tags.map((tag) => ({ key: tag.id, label: tag.name })), [tags]);

    return { tags, options, isLoading, mutate };
};

/** One trainer on a plan's roster, with how much of that plan they actually lead. */
export interface PlanTrainer {
    user_id: number;
    name: string;
    email?: string | null;
    image_id?: number | null;
    /** Groups of this plan they are staffed on. Zero = available but unused. */
    groups_count: number;
    /**
     * The hours they can train, batched in with the roster rather than a
     * per-row request. Empty means nothing was ever stated, which reads as
     * "every day" — see `AvailabilityController::trainerUpdate`.
     */
    availability: ColumnAvailability[];
}

/** A member of the club's staff, as offered when picking a plan's trainers. */
export interface ClubTrainer {
    id: number;
    name: string;
    email?: string | null;
    image_id?: number | null;
    /** Only on a plan's roster: their stated hours, null when unrestricted. */
    availability?: ColumnAvailability[] | null;
}

interface PlanTrainerResponse {
    data: PlanTrainer[];
    meta?: {
        club_trainers: ClubTrainer[];
        /** Trainers staffed on this plan's groups but not on its roster. */
        staffed_missing: number;
    };
}

const planTrainerUrl = (clubSlug?: string, planId?: number) =>
    clubSlug && planId ? `club/${clubSlug}/training-planner/plan/${planId}/trainer` : null;

/**
 * The trainers of one plan, and the club staff they are picked from.
 *
 * Both arrive in one response because the management page needs the second to
 * edit the first, and a second request for a list of names would be a waste.
 */
export const usePlanTrainers = (clubSlug?: string, planId?: number) => {
    const { data, isLoading, mutate } = useSWR<PlanTrainerResponse>(planTrainerUrl(clubSlug, planId));

    return {
        trainers: useMemo(() => data?.data ?? [], [data]),
        clubTrainers: useMemo(() => data?.meta?.club_trainers ?? [], [data]),
        staffedMissing: data?.meta?.staffed_missing ?? 0,
        isLoading,
        mutate
    };
};

/**
 * Trainers offered for a group, an appointment or the grid's trainer axis.
 *
 * Inside a plan this is its roster and nothing else. An empty roster therefore
 * offers nobody — no quiet fallback to the club's staff, because a fallback
 * makes the two states look identical: a plan whose trainers were chosen and a
 * plan where nobody has chosen yet would both show all twenty employees, and
 * the trainer axis would draw a column for each of them either way. Choosing
 * nobody has to look like nobody, or the choice means nothing.
 *
 * Outside a plan the club list stands, because there is no roster to ask.
 */
export const useTrainerOptions = (clubSlug: string, planId?: number) => {
    const { data: plan, isLoading: isLoadingPlan } = useSWR<PlanTrainerResponse>(planTrainerUrl(clubSlug, planId));
    const { data: club, isLoading: isLoadingClub } = useSWR<{ data: ClubTrainer[] }>(
        clubSlug && !planId ? `club/${clubSlug}/relatives/list` : null
    );

    const trainers: ClubTrainer[] = useMemo(() => {
        if (!planId) return club?.data ?? [];

        return (plan?.data ?? []).map((trainer) => ({
            id: trainer.user_id,
            name: trainer.name,
            email: trainer.email,
            image_id: trainer.image_id,
            availability: trainer.availability?.length ? trainer.availability : null
        }));
    }, [club, plan, planId]);

    const options: Option[] = useMemo(
        () => trainers.map((trainer) => ({ key: trainer.id, label: trainer.name })),
        [trainers]
    );

    return {
        trainers,
        options,
        isLoading: planId ? isLoadingPlan : isLoadingClub,
        /** No roster yet: nothing is offered, and the screen should say why. */
        isEmptyRoster: Boolean(planId && !isLoadingPlan && 0 === (plan?.data?.length ?? 0))
    };
};

/** Spaces of the Space Manager, referenced only — never booked by this module. */
export const useSpaceOptions = (clubSlug: string) => {
    const { data, isLoading } = useSWR(clubSlug ? `club/${clubSlug}/space-manager/space` : null);

    const options: Option[] = useMemo(
        () => (data?.data ?? []).map((space: any) => ({ key: space.id, label: space.name })),
        [data]
    );

    return { options, isLoading };
};

/**
 * The club's plans. A plan is a named working set of groups over one period —
 * clubs run several at once, split by audience as much as by season — and it is
 * the only place the season lives.
 */
export const usePlannerPlans = (clubSlug?: string) => {
    const { t } = useTranslation('training-planner');
    const { data, isLoading, mutate } = useSWR<{ data: TrainingPlannerPlan[] }>(
        clubSlug ? `club/${clubSlug}/training-planner/plan` : null
    );
    const plans = useMemo(() => data?.data ?? [], [data]);
    const options: Option[] = useMemo(
        () =>
            plans.map((plan) => ({
                key: plan.id,
                // The period is what distinguishes two similarly named plans, so
                // it belongs in the label rather than behind a second lookup.
                label: plan.season_start ? `${plan.name} (${formatSeason(plan, t('form.plan.openEnd'))})` : plan.name
            })),
        [plans, t]
    );

    return { plans, options, isLoading, mutate };
};

/** "01.09.2025 – 31.05.2026", or an open end when only the start is known. */
export const formatSeason = (plan: Pick<TrainingPlannerPlan, 'season_start' | 'season_end'>, openEnd: string) => {
    const format = (value?: string | null) => (value ? value.split('-').reverse().join('.') : null);

    return [format(plan.season_start), format(plan.season_end) ?? openEnd].filter(Boolean).join(' – ');
};


/**
 * The lanes of one plan. A lane can be linked to a bookable space, but need not
 * be.
 *
 * Per plan rather than per club: a winter plan on three courts and a summer plan
 * on six are the normal case, and renaming a lane for this season must not
 * rewrite what an archived season says happened. The cost is that a new plan
 * starts with none, which is what copying from another plan is for.
 */
export const usePlannerColumns = (clubSlug?: string, planId?: number) => {
    const { t } = useTranslation('training-planner');
    const { data, isLoading, mutate } = useSWR<{
        data: TrainingPlannerColumn[];
        meta?: { space_manager_enabled?: boolean };
    }>(clubSlug && planId ? `club/${clubSlug}/training-planner/plan/${planId}/column` : null);
    const columns = useMemo(() => data?.data ?? [], [data]);
    const options: Option[] = useMemo(
        () => columns.map((column) => ({ key: column.id, label: column.name })),
        [columns]
    );

    /**
     * Options for one appointment. Hidden lanes and lanes closed at that time
     * are left out, because assigning one would save an appointment the grid
     * then cannot draw — and the server refuses it anyway. One that is already
     * assigned stays in the list, marked: otherwise the mismatch would be
     * invisible and unfixable.
     *
     * The hours are optional so a caller that only knows the day still gets the
     * old answer. Passing them is what stops a hall closing at four from
     * looking like a valid choice for a five o'clock training.
     */
    const optionsFor = useCallback(
        (weekday?: number, selected: number[] = [], start?: number, end?: number): Option[] =>
            columns
                .filter((column) => selected.includes(column.id) || isColumnUsable(column, weekday, start, end))
                .map((column) =>
                    isColumnUsable(column, weekday, start, end)
                        ? { key: column.id, label: column.name }
                        : {
                              key: column.id,
                              label: `${column.name} · ${t('form.slots.columnUnavailable')}`,
                              color: 'error' as const
                          }
                ),
        [columns, t]
    );

    return {
        columns,
        options,
        optionsFor,
        isLoading,
        mutate,
        spaceManagerEnabled: data?.meta?.space_manager_enabled ?? false
    };
};

/** Courses of the Trainingsjournal, carrying prices and the default group size. */
export const useTrainingOptions = (clubSlug: string) => {
    const { data, isLoading } = useSWR(clubSlug ? `club/${clubSlug}/training-planner/training-options` : null);

    const options: Option[] = useMemo(
        () => (data?.data ?? []).map((training: any) => ({ key: training.id, label: training.name })),
        [data]
    );

    return { options, isLoading };
};

/**
 * Every group of one plan, for a filter or select.
 *
 * The group listing is paginated and therefore unusable as an options source.
 * A migrated group carries no name, so the label falls back to its appointment —
 * "Di 16:00" is what identifies it in the grid too, and inventing a name here
 * would be the one place in the module that does.
 */
export const useGroupOptions = (clubSlug?: string, planId?: number) => {
    const { t } = useTranslation('training-planner');
    const { data, isLoading } = useSWR<{ data: Group[] }>(
        clubSlug && planId ? `club/${clubSlug}/training-planner/group-options?plan_id=${planId}` : null
    );

    const options: Option[] = useMemo(
        () =>
            (data?.data ?? []).map((group) => ({
                key: group.id,
                label:
                    groupLabel(group.name) ??
                    (group.slots ?? [])
                        .map((slot) => `${t(`weekdaysShort.${slot.weekday}`)} ${slot.start_time.slice(0, 5)}`)
                        .join(', ') ??
                    ''
            })),
        [data, t]
    );

    return { groups: data?.data ?? [], options, isLoading };
};

/** Weekday options in ISO order, translated. */
export const useWeekdayOptions = () => {
    const { t } = useTranslation('training-planner');

    return useMemo<Option[]>(
        () =>
            [1, 2, 3, 4, 5, 6, 7].map((weekday) => ({
                key: weekday,
                label: t(`weekdays.${weekday}`)
            })),
        [t]
    );
};
